import type { Doc } from "building-types/doc";
import type { ContentTemplateInstanceHandle } from "building-types/handles/docEntityHandleTypes";
import type {
  AnnotationDrawingTemplate,
  ContentTemplate,
  ContentTemplateInstance,
  DrawingTemplate,
  GenericDrawingTemplate,
  TagTemplate,
  ViewportTitleDrawingTemplate,
} from "building-types/views/planSets";
import { snakeCaseToTitleCase } from "common/utils/strings";
import { SystemDrawingTemplates } from "./systemDrawingTemplates";

export function contentTemplateViewScale(template: ContentTemplate): number {
  return isGenericDrawingTemplate(template) || isAnnotationDrawingTemplate(template) ? template.viewScale : 1;
}

export function isDrawingTemplate(contentTemplate: ContentTemplate): contentTemplate is DrawingTemplate {
  return contentTemplate.templateType === "DRAWING";
}

export function isViewportTitleDrawingTemplate(
  contentTemplate: ContentTemplate,
): contentTemplate is ViewportTitleDrawingTemplate {
  return isDrawingTemplate(contentTemplate) && contentTemplate.drawingType === "VIEWPORT_TITLE";
}

export function isTagTemplate(contentTemplate: ContentTemplate): contentTemplate is TagTemplate {
  return isDrawingTemplate(contentTemplate) && contentTemplate.drawingType === "TAG";
}

function makeTagTemplateEntityPredicate<T extends TagTemplate["targetEntityType"]>(targetType: T) {
  return (contentTemplate: ContentTemplate): contentTemplate is TagTemplate & { targetEntityType: T } => {
    return isTagTemplate(contentTemplate) && contentTemplate.targetEntityType === targetType;
  };
}

export const TAG_TEMPLATE_ENTITY_PREDICATES: {
  readonly [entityType in TagTemplate["targetEntityType"]]: ReturnType<
    typeof makeTagTemplateEntityPredicate<entityType>
  >;
} = {
  ROOM: makeTagTemplateEntityPredicate("ROOM"),
};

export function isGenericDrawingTemplate(contentTemplate: ContentTemplate): contentTemplate is GenericDrawingTemplate {
  return isDrawingTemplate(contentTemplate) && contentTemplate.drawingType === undefined;
}

export function isAnnotationDrawingTemplate(
  contentTemplate: ContentTemplate,
): contentTemplate is AnnotationDrawingTemplate {
  return isDrawingTemplate(contentTemplate) && contentTemplate.drawingType === "ANNOTATION";
}

export function isDrawingTemplateInstance(
  handle: ContentTemplateInstanceHandle,
  doc: Pick<Doc, "contentTemplateInstances">,
) {
  const instance = doc.contentTemplateInstances[handle.id];
  if (!instance) {
    return false;
  }
  return instance.templateType === "DRAWING";
}

export function isValidParentForDrawingTemplateInstance(
  instanceTemplate: DrawingTemplate,
  parentTemplate?: ContentTemplate | ContentTemplateInstance | null,
): boolean {
  if (parentTemplate?.entityType === "CONTENT_TEMPLATE" && isDrawingTemplate(parentTemplate)) {
    if (!contentTemplateAllowsNestedInstances(parentTemplate)) {
      // Drawing templates cannot be nested inside viewport titles or tags
      return false;
    }
    return (
      // Annotation drawing templates can be hosted on generic drawing templates
      (isGenericDrawingTemplate(parentTemplate) && isAnnotationDrawingTemplate(instanceTemplate)) ||
      // drawing templates can be hosted to other drawing templates of the same type
      parentTemplate.drawingType === instanceTemplate.drawingType
    );
  }
  if (!parentTemplate) {
    // Only annotation drawing templates can be hosted to a model or a view
    return isAnnotationDrawingTemplate(instanceTemplate);
  }

  // generic drawing templates can be hosted to a sheet template or sheet template instance
  return isGenericDrawingTemplate(instanceTemplate);
}

export function isSystemContentTemplateId(id: string): boolean {
  return !!SystemDrawingTemplates[id];
}
export function getContentTemplateSubType(template: ContentTemplate | undefined) {
  if (!template) {
    return "Content";
  }

  if (template.templateType === "SHEET") {
    return "Sheet";
  }

  if (!template.drawingType) {
    return "Drawing";
  }

  return snakeCaseToTitleCase(template.drawingType);
}

export function contentTemplateAllowsNestedInstances(template: ContentTemplate): boolean {
  return !isViewportTitleDrawingTemplate(template) && !isTagTemplate(template);
}

export function contentTemplateAllowsViewportOrDataTable(template: ContentTemplate): boolean {
  return (
    !isViewportTitleDrawingTemplate(template) && !isTagTemplate(template) && !isAnnotationDrawingTemplate(template)
  );
}
